import { useTranslation } from "react-i18next";
import { Combobox, type ComboboxOption } from "@tellescope/ui";
import { useMemo } from "react";
import { levenshteinDistance, US_STATES } from "@tellescope/utils";

export interface StateSelectFieldProps {
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  id?: string;
}

export const StateSelectField = ({ value, onValueChange, className, id }: StateSelectFieldProps) => {
  const { t } = useTranslation("forms");

  // Find the selected option based on the value
  const selectedOption = useMemo(() => US_STATES.find((option) => option.value === value), [value]);

  // Handle change by extracting the value from the ComboboxOption
  const handleChange = (option: ComboboxOption | undefined) => {
    onValueChange?.(option?.value ?? "");
  };

  return (
    <Combobox
      value={selectedOption}
      onChange={handleChange}
      options={US_STATES}
      placeholder={t("Select a state")}
      searchPlaceholder={t("Search states...")}
      className={className}
      customFilter={(searchTerm, options) => {
        return options
          .filter((option) =>
            `${option.label.toLowerCase()}|${option.value.toLowerCase()}`.includes(searchTerm.toLowerCase())
          )
          .sort((a, b) => {
            const lowerSearch = searchTerm.toLowerCase();
            const aMinDistance = Math.min(
              levenshteinDistance(lowerSearch, a.label.toLowerCase()),
              levenshteinDistance(lowerSearch, a.value.toLowerCase())
            );
            const bMinDistance = Math.min(
              levenshteinDistance(lowerSearch, b.label.toLowerCase()),
              levenshteinDistance(lowerSearch, b.value.toLowerCase())
            );
            return aMinDistance - bMinDistance;
          });
      }}
      id={id}
    />
  );
};
